import { useEffect, useState } from 'react';
import { errorText } from '../api';
import { formatDateTimeShort } from '../format';
import { adminApi, type GoogleConnection, type GoogleState } from './api';

/**
 * „Google-Kalender“ (Konzept 5): verbinden, Kalender ankreuzen, die sperren, den Kalender wählen, in dem Buchungen
 * landen, trennen. Dazu der Hinweis zur iPhone-Falle (5.4). Nur mit Login; ein Schlüssel oder eine KI kommt hier nicht ran.
 */
export function GooglePage() {
  const [state, setState] = useState<GoogleState | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(() => messageFromUrl());

  const load = () =>
    adminApi
      .getGoogle()
      .then(setState)
      .catch((e) => setMessage({ kind: 'error', text: errorText(e) }));
  useEffect(() => {
    void load();
    // Die Rückkehr von Google hängt ?status=… an – nach dem Lesen wieder weg damit.
    if (window.location.search) window.history.replaceState(null, '', window.location.pathname);
  }, []);

  async function run(fn: () => Promise<void>) {
    setBusy(true);
    setMessage(null);
    try {
      await fn();
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
    } finally {
      setBusy(false);
    }
  }

  const connection = state?.connection ?? null;

  const toggleBusy = (id: string, checked: boolean) =>
    run(async () => {
      if (!connection) return;
      const ids = checked ? [...connection.busyCalendarIds, id] : connection.busyCalendarIds.filter((x) => x !== id);
      const updated = await adminApi.saveGoogle({ busyCalendarIds: ids });
      setState({ configured: true, connection: updated });
    });

  const setTarget = (id: string) =>
    run(async () => {
      const updated = await adminApi.saveGoogle({ targetCalendarId: id });
      setState({ configured: true, connection: updated });
      setMessage({ kind: 'ok', text: 'Gespeichert. Neue Buchungen landen ab jetzt in diesem Kalender.' });
    });

  const disconnect = () => {
    if (!window.confirm('Verbindung trennen? Neue Buchungen landen dann nicht mehr im Google-Kalender und bekommen keinen Meet-Link. Bestehende Termine bleiben in Google stehen.')) return;
    void run(async () => {
      await adminApi.disconnectGoogle();
      setMessage({ kind: 'ok', text: 'Verbindung getrennt.' });
      await load();
    });
  };

  const retry = () =>
    run(async () => {
      const r = await adminApi.retryGoogle();
      setState(r.view);
      setMessage({ kind: 'ok', text: r.done ? `${r.done === 1 ? 'Ein Termin' : `${r.done} Termine`} nachgetragen.` : r.retried || r.failed ? 'Das hat noch nicht geklappt – EverCal versucht es von selbst weiter.' : 'Nichts offen.' });
    });

  return (
    <>
      <div className="admin-head">
        <div>
          <h1>Google-Kalender</h1>
          <p className="muted">Jede Buchung landet mit Meet-Link in deinem Google-Kalender – und was dort schon steht, ist auf der Buchungsseite gesperrt.</p>
        </div>
      </div>
      {message && <div className={message.kind === 'ok' ? 'success' : 'error'}>{message.text}</div>}
      <div className="admin-grid">
        <div className="card">
          <h2 className="card__title">Verbindung</h2>
          {state === null && <p className="muted small">Lade …</p>}
          {state && !state.configured && (
            <>
              <p>
                <span className="tag tag--muted">noch nicht eingerichtet</span>
              </p>
              <p className="muted small">Dem Server fehlen noch die Zugangsdaten des Google-Cloud-Projekts (GOOGLE_CLIENT_ID und GOOGLE_CLIENT_SECRET). Bis dahin läuft alles wie bisher – nur ohne Google. Wie es eingerichtet wird, steht in <code>docs/google-einrichten.md</code>.</p>
            </>
          )}
          {state?.configured && !connection && (
            <>
              <p>
                <span className="tag tag--muted">nicht verbunden</span>
              </p>
              <p className="muted small">Verbinde dein Google-Konto. Google fragt dich einmal, ob EverCal Termine anlegen und deine Kalender sehen darf. Kommt der Hinweis „Diese App wurde nicht überprüft“, klick auf „Erweitert“ und fahr fort – das ist bei einer eigenen App so.</p>
              <a className="button button--small" href="/api/admin/google/connect">
                Mit Google verbinden
              </a>
            </>
          )}
          {connection && <ConnectionStatus connection={connection} busy={busy} onDisconnect={disconnect} onRetry={retry} />}
        </div>

        <div className="card">
          <h2 className="card__title">Das iPhone trägt sonst in iCloud ein</h2>
          <p className="small">Von Haus aus speichert die Kalender-App auf dem iPhone neue Termine in iCloud – und iCloud sieht EverCal nicht. Damit ein Termin vom iPhone die Zeit auf der Buchungsseite sperrt, muss er im Google-Kalender landen:</p>
          <ol className="steps-list small">
            <li>Google-Konto auf dem iPhone als Kalender-Account einbinden (Einstellungen → Apps → Kalender → Accounts).</li>
            <li>Google als Standardkalender setzen: Einstellungen → Apps → Kalender → Standardkalender.</li>
            <li>Beim nächsten neuen Termin prüfen, ob unter „Kalender“ der Google-Kalender steht.</li>
          </ol>
          <p className="muted small">Bestehende iCloud-Termine bleiben, wo sie sind. Die wichtigen kommenden überträgst du einmal von Hand.</p>
        </div>

        {connection && (
          <div className="card card--wide">
            <h2 className="card__title">Deine Kalender</h2>
            <p className="muted small">
              <strong>Sperrt:</strong> Was in diesem Kalender steht, ist auf der Buchungsseite nicht buchbar. <strong>Buchungen landen hier:</strong> In genau einen Kalender schreibt EverCal die Termine – der sperrt immer.
              {connection.calendarsStale && ' Die Liste konnte gerade nicht frisch geladen werden; gezeigt wird der letzte Stand.'}
            </p>
            <div>
              {connection.calendars.map((c) => (
                <div key={c.id} className="calendar-row">
                  <div>
                    <strong>{c.summary}</strong>
                    {c.primary && <span className="muted small"> · Hauptkalender</span>}
                  </div>
                  <label>
                    <input type="checkbox" checked={c.busy} disabled={busy || c.target} onChange={(e) => void toggleBusy(c.id, e.target.checked)} />
                    sperrt
                  </label>
                  <label>
                    <input type="radio" name="target" checked={c.target} disabled={busy} onChange={() => void setTarget(c.id)} />
                    Buchungen landen hier
                  </label>
                </div>
              ))}
            </div>
            <p className="muted small">Ganztägige Einträge („Urlaub“, „Messe“) sperren den ganzen Tag. Willst du an so einem Tag trotzdem Zeiten anbieten, gib sie unter „Tage“ frei – das gewinnt. Einträge mit Uhrzeit bleiben auch dann gesperrt.</p>
          </div>
        )}
      </div>
    </>
  );
}

function ConnectionStatus({ connection, busy, onDisconnect, onRetry }: { connection: GoogleConnection; busy: boolean; onDisconnect: () => void; onRetry: () => void }) {
  const needsReauth = connection.status === 'needs_reauth';
  return (
    <>
      <p>
        {needsReauth ? <span className="tag tag--warn">Verbindung erneuern</span> : <span className="tag tag--ok">verbunden</span>} <span className="small">als {connection.email}</span>
      </p>
      {needsReauth && (
        <>
          <p className="small">Google lässt EverCal gerade nicht mehr an deinen Kalender – meist nach einer Passwortänderung oder weil der Zugriff entzogen wurde. Verbinde einmal neu; danach trägt EverCal alles Offene von selbst nach.</p>
          <a className="button button--small" href="/api/admin/google/connect">
            Verbindung erneuern
          </a>
        </>
      )}
      {!needsReauth && connection.lastError && <p className="muted small">Zuletzt gab es ein Problem: {connection.lastError}. EverCal versucht es von selbst weiter.</p>}
      {connection.pendingJobs > 0 && (
        <p className="small">
          {connection.pendingJobs === 1 ? 'Ein Termin wartet' : `${connection.pendingJobs} Termine warten`} noch darauf, in den Kalender zu kommen.{' '}
          {!needsReauth && (
            <button type="button" className="link-button" disabled={busy} onClick={onRetry}>
              Jetzt nachtragen
            </button>
          )}
        </p>
      )}
      <p className="muted small">
        Verbunden seit {formatDateTimeShort(connection.connectedAt)}.{connection.lastCheckedAt && <> Zuletzt geprüft {formatDateTimeShort(connection.lastCheckedAt)}.</>}
      </p>
      <div className="save-bar">
        {!needsReauth && (
          <a className="button button--secondary button--small" href="/api/admin/google/connect">
            Neu verbinden
          </a>
        )}
        <button type="button" className="link-button" disabled={busy} onClick={onDisconnect}>
          Trennen
        </button>
      </div>
    </>
  );
}

/** Die Rückkehr von Google landet mit ?status=… hier. */
function messageFromUrl(): { kind: 'ok' | 'error'; text: string } | null {
  const q = new URLSearchParams(window.location.search);
  const status = q.get('status');
  if (!status) return null;
  switch (status) {
    case 'connected':
      return { kind: 'ok', text: 'Verbunden. Neue Buchungen landen ab jetzt in deinem Google-Kalender.' };
    case 'denied':
      return { kind: 'error', text: 'Du hast den Zugriff bei Google nicht erlaubt – ohne ihn geht es nicht. Versuch es gern noch einmal.' };
    case 'state':
      return { kind: 'error', text: 'Der Rückweg von Google passte nicht zu deiner Anmeldung. Bitte noch einmal „Mit Google verbinden“ klicken.' };
    case 'unconfigured':
      return { kind: 'error', text: 'Google ist auf dem Server noch nicht eingerichtet.' };
    default:
      return { kind: 'error', text: q.get('message') || 'Das Verbinden hat nicht geklappt. Bitte versuch es noch einmal.' };
  }
}
